import { useRef } from "react";
import { RefreshCw, User, Lock, Eye, EyeOff, Save, Camera, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import TwoFASection from "./TwoFASection";
import PasskeysSection from "./PasskeysSection";

export default function SettingsContent({
  adminUsername, currentPassword, setCurrentPassword,
  newUsername, setNewUsername, newPassword, setNewPassword,
  confirmPassword, setConfirmPassword, showCurrentPassword, setShowCurrentPassword,
  showNewPassword, setShowNewPassword, isSaving, saveCredentials, token,
  adminAvatar, uploadAvatar, deleteAvatar
}) {
  const fileInputRef = useRef(null);

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500000) {
      alert("Image trop volumineuse (max 500KB)");
      return;
    }
    uploadAvatar(file);
    e.target.value = "";
  };

  return (
    <div className="max-w-lg mx-auto space-y-6">
      {/* Avatar + Credentials Section */}
      <div className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-xl p-4 sm:p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="relative group">
            {adminAvatar ? (
              <img src={adminAvatar} alt="Avatar" className="w-14 h-14 rounded-full object-cover border-2 border-amber-500/30" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center border-2 border-amber-500/30">
                <User className="w-7 h-7 text-amber-500" />
              </div>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            >
              <Camera className="w-5 h-5 text-white" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleAvatarChange}
              className="hidden"
            />
          </div>
          <div className="flex-1">
            <h2 className="text-white font-medium">Compte Admin</h2>
            <p className="text-xs text-zinc-500">Modifier vos identifiants de connexion</p>
            <div className="flex gap-2 mt-1.5">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-[10px] text-cyan-400 hover:text-cyan-300"
              >
                Changer l'avatar
              </button>
              {adminAvatar && (
                <button onClick={deleteAvatar} className="text-[10px] text-red-400 hover:text-red-300">
                  Supprimer
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-zinc-400 mb-1.5 block">Nom d'utilisateur actuel</label>
            <div className="px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-md text-sm text-zinc-300">
              {adminUsername || "admin"}
            </div>
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1.5 block">
              Mot de passe actuel <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-10 pr-10 bg-zinc-900 border-zinc-700 text-white text-sm"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
              >
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <hr className="border-zinc-800" />

          <div>
            <label className="text-xs text-zinc-400 mb-1.5 block">Nouveau nom d'utilisateur</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input type="text" value={newUsername} onChange={(e) => setNewUsername(e.target.value)}
                placeholder="Laisser vide pour ne pas changer"
                className="pl-10 bg-zinc-900 border-zinc-700 text-white text-sm" />
            </div>
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1.5 block">Nouveau mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input type={showNewPassword ? "text" : "password"} value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Laisser vide pour ne pas changer"
                className="pl-10 pr-10 bg-zinc-900 border-zinc-700 text-white text-sm" />
              <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {newPassword && (
            <div>
              <label className="text-xs text-zinc-400 mb-1.5 block">Confirmer le nouveau mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input type={showNewPassword ? "text" : "password"} value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirmer le mot de passe"
                  className={`pl-10 bg-zinc-900 border-zinc-700 text-white text-sm ${confirmPassword && confirmPassword !== newPassword ? "border-red-500" : ""}`} />
              </div>
              {confirmPassword && confirmPassword !== newPassword && (
                <p className="text-xs text-red-400 mt-1">Les mots de passe ne correspondent pas</p>
              )}
            </div>
          )}

          <Button onClick={saveCredentials}
            disabled={isSaving || !currentPassword || (newPassword && newPassword !== confirmPassword)}
            className="w-full bg-amber-500 hover:bg-amber-600 text-black text-sm mt-2">
            {isSaving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Sauvegarder
          </Button>
        </div>
      </div>

      {/* 2FA Section */}
      <TwoFASection token={token} />

      {/* Passkeys Section */}
      <PasskeysSection token={token} />
    </div>
  );
}
